import { toast } from '@/components/UI/Toast';
import { useRefreshClaims } from '@/hooks/useRefreshClaims';
import { useMutation } from '@tanstack/react-query';

import { switchToCompany } from '../api/companySwitch.api';
import { SwitchToCompanyRequest } from '../types/CompanySwitch.types';

/**
 * Hook to switch companies and refresh claims.
 *
 * Flow:
 *  1. POST to backend → inserts CompanySwitch record
 *  2. Clear MSAL cache + React Query cache
 *  3. Redirect to B2C (silent SSO) → Azure Function reads CompanySwitches
 *     table and issues a fresh token with the new extension_companyid claim
 */
export const useSwitchCompany = () => {
  const { refreshClaims } = useRefreshClaims();

  return useMutation({
    mutationFn: async (request: SwitchToCompanyRequest) => {
      const response = await switchToCompany(request);

      if (!response.success) {
        throw new Error('Failed to switch company');
      }

      await refreshClaims();

      return response;
    },
    onError: (error) => {
      console.error('Company switch failed:', error);
      toast({
        title: 'Company switch failed',
        description: 'Unable to switch company. Please try again.',
        color: 'danger'
      });
    }
  });
};
